import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { NavigationButton } from "lib/components/Buttons/buttons";

import EditIcon from "@mui/icons-material/Edit";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import WebIcon from "@mui/icons-material/Web";
import DescriptionIcon from "@mui/icons-material/Description";
import BadgeIcon from "@mui/icons-material/Badge";

import { PageLayout } from "lib";

import { UsersAPI, CompaniesAPI } from "api_inbusiness";
import { LoadingSpinner } from "lib";

import { useUser } from "hooks/useUser";
import { Helmet } from "react-helmet";
import DefaultUserPicture from "resources/img/user/DefaultUserPicture.png";
import DefaultCompanyPicture from "resources/img/InBusiness/no_business_logo.jpeg";
import { useCallback } from "react";

import ChangeProfilePicture from "pages/Profile/ChangeProfilePicture";
import styles from "./User.module.scss";

const User = () => {
  const { id } = useParams();
  const [open, setOpen] = useState(false);

  const [userState, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userPhoto, setUserPhoto] = React.useState(null);
  const [companyLogo, setCompanyLogo] = React.useState([]);
  const { user } = useUser();

  const getUserById = useCallback(() => {
    UsersAPI.getById(id).then((res) => {
      setUserState(res.data);
      getUserImage(res.data);
      getCompaniesLogo(res.data);
      setLoading(false);
    });
  }, [id]);

  useEffect(() => {
    getUserById();
  }, [getUserById]);

  const getUserImage = (userState) => {
    const imgId = userState.avatarId;
    if (imgId) {
      UsersAPI.getNewUserPicture(imgId).then((res) => {
        if (res.ok === true) {
          setUserPhoto(res.data);
        }
      });
    }
  };

  const getCompaniesLogo = async (userState) => {
    const logos = [];
    for (const lg of userState.companies) {
      try {
        const res = await new Promise((resolve, reject) => {
          CompaniesAPI.getNewLogoCompanies(lg.logoId).then((res) => {
            if (res.ok === true) {
              resolve(res.data);
            } else {
              reject("Failed to fetch company logo");
            }
          });
        });
        logos.push(res);
      } catch (error) {
        logos.push(false);
      }
    }
    setCompanyLogo(logos);
  };

  return (
    <>
      <Helmet>
        <title>
          {userState 
            ? `InBUSINESS CRM :: ${userState.firstName} ${userState.lastName}` 
            : 'InBUSINESS CRM :: User'}
        </title>
      </Helmet>
      <PageLayout
        title={userState?.firstName + " " + userState?.lastName}
        backArrow={true}
        backUrl="/users"
      >
        {loading ? (
          <LoadingSpinner loading={loading} margin="20% auto" />
        ) : (
          <div className={styles.wrapper}>
            {/* Profile Header Card */}
            <div className={styles.profileCard}>
              <div className={styles.avatarSection}>
                <div className={styles.avatarContainer}>
                  <img
                    alt={`${userState.firstName} ${userState.lastName}`}
                    src={userPhoto || DefaultUserPicture}
                    className={styles.avatar}
                    loading="lazy"
                  />
                </div>
              </div>
              
              <div className={styles.profileInfo}>
                <h2 className={styles.memberName}>
                  {userState.firstName} {userState.lastName}
                </h2>
                <p className={styles.memberType}>
                  {userState.type === 'MEMBER' ? 'Membru Activ' : userState.type}
                </p>
                {userState.county && (
                  <p className={styles.memberLocation}>
                    📍 {userState.county.name}
                  </p>
                )}
              </div>

              {userState.id === user.id && (
                <div className={styles.editButtonContainer}>
                  <NavigationButton
                    variant="contained"
                    color="blue"
                    startIcon={<EditIcon />}
                    onClick={() => setOpen(true)}
                  >
                    {"Modifică Profil"}
                  </NavigationButton>
                </div>
              )}
            </div>

            {/* Contact Information Card */}
            <div className={styles.contactCard}>
              <h3 className={styles.sectionTitle}>Informații de Contact</h3>
              <div className={styles.contactGrid}>
                <div className={styles.contactItem}>
                  <div className={styles.contactIcon}>📧</div>
                  <div className={styles.contactDetails}>
                    <span className={styles.contactLabel}>Email</span>
                    <a href={`mailto:${userState.email}`} className={styles.contactValue}>
                      {userState.email}
                    </a>
                  </div>
                </div>
                
                <div className={styles.contactItem}>
                  <div className={styles.contactIcon}>📱</div>
                  <div className={styles.contactDetails}>
                    <span className={styles.contactLabel}>Telefon</span>
                    <a href={`tel:${userState.phone}`} className={styles.contactValue}>
                      {userState.phone}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Companies Section */}
            {userState?.companies && userState.companies.length > 0 && (
              <div className={styles.companiesSection}>
                <h3 className={styles.sectionTitle}>
                  Companii ({userState.companies.length})
                </h3>
                <div className={styles.companiesGrid}>
                  {userState.companies.map((comp, index) => (
                    <UserCompanies
                      key={index}
                      company={comp}
                      logo={companyLogo[index]}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </PageLayout>
      {userState && (
        <ChangeProfilePicture
          open={open}
          setOpen={setOpen}
          userData={userState}
          getUserById={getUserById}
          userPhoto={userPhoto}
        />
      )}
    </>
  );
};

const UserCompanies = ({ company, logo }) => {
  const { name, site, description, phone } = company;
  const [isExpanded, setIsExpanded] = React.useState(false);
  
  // Check if description is long enough to need truncation (approximately 150 chars = ~3 lines)
  const needsTruncation = description && description.length > 150;

  return (
    <div className={styles.companyCard}>
      <div className={styles.companyHeader}>
        <div className={styles.companyLogoWrapper}>
          <img
            src={logo || DefaultCompanyPicture}
            alt={name}
            className={styles.companyLogo}
          />
        </div>
        <div className={styles.companyMainInfo}>
          <h4 className={styles.companyName}>{name}</h4>
          {phone && (
            <a href={`tel:${phone}`} className={styles.companyContact}>
              <PhoneAndroidIcon /> {phone}
            </a>
          )}
          {site && (
            <a
              href={`https://${site}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.companyContact}
            >
              <WebIcon /> {site}
            </a>
          )}
        </div>
      </div>

      {description && (
        <div className={styles.companyDescriptionSection}>
          <div className={styles.descriptionHeader}>
            <DescriptionIcon className={styles.descIcon} />
            <span style={{ fontSize: '0.85rem', color: '#7f8c8d', fontWeight: 500 }}>Descriere</span>
          </div>
          <p className={`${styles.companyDescription} ${isExpanded ? styles.expanded : ''}`}>
            {description}
          </p>
          {needsTruncation && (
            <button 
              className={`${styles.readMoreButton} ${isExpanded ? styles.expanded : ''}`}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Mai puțin' : 'Citește mai mult'}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default User;
